import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client.js';

/**
 * Calls on a case: the recording, its transcript and the automatic QA score.
 *
 * THE TRANSCRIPT IS NOT FETCHED UNTIL SOMEBODY ASKS FOR IT. Reading it decrypts
 * it and writes audit.phi_access_log, and a page that logs a PHI read merely
 * because it rendered makes the access log worthless — you can no longer tell
 * who actually read a patient's emergency call from who happened to open the
 * case. So it is a button, and pressing it is the access.
 *
 * A FAILED SCORE IS SHOWN WITH ITS REASONS, NEVER AS A BARE NUMBER. Every item
 * carries the evidence that produced it, because this is performance data about
 * a named colleague and they have to be able to argue with it.
 */

interface CaseCall {
  id: string;
  direction: string;
  status: string;
  from_e164: string | null;
  talk_seconds: number | null;
  recording_document_id: string | null;
  started_at: string;
  transcript_status: string | null;
  word_count: number | null;
  percentage: string | number | null;
  passed: boolean | null;
  critical_failures: number | null;
  skipped_count: number | null;
  reviewed: boolean | null;
}

interface QaItem {
  key: string;
  label: string;
  outcome: 'pass' | 'fail' | 'skip';
  weight: number;
  isCritical: boolean;
  evidence: string;
  guidance?: string | null;
}

function duration(seconds: number | null): string {
  if (seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

function TranscriptBlock({ callId }: { callId: string }) {
  const [open, setOpen] = useState(false);

  const { data, isFetching, error } = useQuery({
    queryKey: ['transcript', callId],
    queryFn: () =>
      api.get<{ status: string; text: string | null; error?: string | null; wordCount?: number }>(
        `/calls/${callId}/transcript`,
      ),
    // The whole point: no request until the button is pressed.
    enabled: open,
    // And no silent refetch afterwards — each one is another logged PHI read.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 rounded border border-line px-2 py-1 text-[11px] text-slate-300 hover:border-slate-500 hover:text-slate-100"
      >
        Show transcript
        <span className="ml-1.5 text-slate-500">· logged as a PHI access</span>
      </button>
    );
  }

  if (isFetching) return <p className="mt-2 text-xs text-slate-400">Decrypting…</p>;
  if (error) return <p className="mt-2 text-xs text-red-400">{error.message}</p>;

  if (!data?.text) {
    const label =
      data?.status === 'unavailable'
        ? 'Transcription is not switched on for this deployment.'
        : data?.status === 'failed'
          ? `Transcription failed: ${data.error ?? 'unknown error'}`
          : data?.status === 'pending'
            ? 'Transcribing…'
            : 'Not transcribed.';
    return <p className="mt-2 text-xs text-slate-400">{label}</p>;
  }

  return (
    <div className="mt-2 max-h-64 overflow-y-auto rounded border border-line bg-surface-3 p-2.5">
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-300">{data.text}</p>
    </div>
  );
}

function ScoreBlock({ callId, summary }: { callId: string; summary: CaseCall }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { data } = useQuery({
    queryKey: ['qa', callId],
    queryFn: () => api.get<{ status: string; score: ScorePayload | null }>(`/calls/${callId}/qa`),
    enabled: expanded,
  });

  const rescore = useMutation({
    mutationFn: () => api.post(`/calls/${callId}/qa/rescore`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['qa', callId] });
      void qc.invalidateQueries({ queryKey: ['case-calls'] });
    },
  });

  const pct = summary.percentage === null ? null : Number(summary.percentage);
  const passed = summary.passed;

  if (passed === null || passed === undefined) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <span className="text-xs text-slate-500">Not scored</span>
        <button
          onClick={() => rescore.mutate()}
          disabled={rescore.isPending}
          className="rounded border border-line px-2 py-0.5 text-[11px] text-slate-400 hover:text-slate-200 disabled:opacity-40"
        >
          {rescore.isPending ? 'Scoring…' : 'Score now'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
      >
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            passed ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'
          }`}
        >
          QA {passed ? 'pass' : 'fail'}
        </span>
        {/* Null percentage means nothing could be checked. Showing 0% there
            would read as "the agent did everything wrong". */}
        <span className="text-xs text-slate-300">
          {pct === null ? 'not measurable' : `${pct}%`}
        </span>
        {summary.critical_failures ? (
          <span className="text-[11px] text-red-400">{summary.critical_failures} critical</span>
        ) : null}
        {summary.skipped_count ? (
          <span className="text-[11px] text-slate-500">{summary.skipped_count} skipped</span>
        ) : null}
        <span className="ml-auto text-[11px] text-slate-500">{expanded ? 'Hide' : 'Details'}</span>
      </button>

      {expanded && data?.score && (
        <div className="mt-2 space-y-1.5 rounded border border-line bg-surface-3 p-2.5">
          {data.score.results.map((r) => (
            <div key={r.key} className="flex gap-2 text-xs">
              <span
                className={
                  r.outcome === 'pass'
                    ? 'text-emerald-400'
                    : r.outcome === 'fail'
                      ? 'text-red-400'
                      : 'text-slate-600'
                }
              >
                {r.outcome === 'pass' ? '✓' : r.outcome === 'fail' ? '✕' : '–'}
              </span>
              <div className="min-w-0">
                <p className={r.outcome === 'skip' ? 'text-slate-500' : 'text-slate-300'}>
                  {r.label}
                  {r.isCritical && r.outcome === 'fail' && (
                    <span className="ml-1.5 text-[10px] font-semibold uppercase text-red-400">
                      critical
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-slate-500">{r.evidence}</p>
                {r.outcome === 'fail' && r.guidance && (
                  <p className="mt-0.5 text-[11px] text-sky-400/80">{r.guidance}</p>
                )}
              </div>
            </div>
          ))}

          {data.score.review ? (
            <p className="border-t border-line pt-2 text-[11px] text-slate-400">
              Reviewed {data.score.review.outcome} by {data.score.review.by}
              {data.score.review.note ? ` — ${data.score.review.note}` : ''}
            </p>
          ) : (
            <ReviewButtons callId={callId} />
          )}
        </div>
      )}
    </div>
  );
}

interface ScorePayload {
  percentage: number | null;
  passed: boolean;
  results: QaItem[];
  review: { outcome: string; note: string | null; by: string | null; at: string } | null;
}

/**
 * Uphold or overturn.
 *
 * Present on every score, not only failures: a supervisor who disagrees with a
 * pass needs the same lever. An automatic verdict nobody can overturn is one
 * people learn to game.
 */
function ReviewButtons({ callId }: { callId: string }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');

  const review = useMutation({
    mutationFn: (outcome: string) => api.post(`/calls/${callId}/qa/review`, { outcome, note }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['qa', callId] }),
  });

  return (
    <div className="border-t border-line pt-2">
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional)"
        className="w-full rounded border border-line bg-surface-2 px-2 py-1 text-xs text-slate-200 placeholder:text-slate-600"
      />
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {(['upheld', 'overturned', 'coaching', 'escalated'] as const).map((o) => (
          <button
            key={o}
            onClick={() => review.mutate(o)}
            disabled={review.isPending}
            className="rounded border border-line px-2 py-0.5 text-[11px] capitalize text-slate-300 hover:border-slate-500 hover:text-slate-100 disabled:opacity-40"
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CallQaPanel({ caseId }: { caseId: string }) {
  const { data } = useQuery({
    queryKey: ['case-calls', caseId],
    queryFn: () => api.get<{ calls: CaseCall[] }>(`/cases/${caseId}/calls`),
  });

  const calls = data?.calls ?? [];
  if (!calls.length) return null;

  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Calls</h3>
      <div className="space-y-3">
        {calls.map((call) => (
          <div key={call.id} className="rounded-lg bg-surface-3 p-3">
            <div className="flex flex-wrap items-baseline gap-2 text-xs">
              <span className="font-mono text-slate-200">{call.from_e164 ?? 'withheld'}</span>
              <span className="text-slate-500">{call.direction}</span>
              <span className="text-slate-400">{duration(call.talk_seconds)}</span>
              <span className="ml-auto text-slate-500">
                {new Date(call.started_at).toLocaleString('en-ZA')}
              </span>
            </div>

            {call.recording_document_id ? (
              <audio
                // preload="none" so opening a case does not pull every recording
                // on it down the wire before anyone has asked to hear one.
                preload="none"
                controls
                src={`/api/v1/documents/${call.recording_document_id}/content`}
                className="mt-2 h-8 w-full"
              />
            ) : (
              <p className="mt-2 text-xs text-slate-500">No recording</p>
            )}

            {call.recording_document_id && <TranscriptBlock callId={call.id} />}
            <ScoreBlock callId={call.id} summary={call} />
          </div>
        ))}
      </div>
    </div>
  );
}
